/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Command } from "cmdk";
import { Loader2, User } from "lucide-react";
import { navItems } from "@/lib/navigation";
import { getPeriodNavigationSearch } from "@/lib/periodNavigation";
import { supabase } from "@/lib/supabase";

const TOGGLE_EVENT = "admin-command-palette-toggle";
const DEBOUNCE_MS = 300;
const MAX_USER_RESULTS = 5;

export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent(TOGGLE_EVENT));
}

interface UserResult {
  id: string;
  name: string;
  email: string;
  plan: string | null;
}

function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getLevenshteinDistance(left: string, right: string): number {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const rows = left.length + 1;
  const cols = right.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));

  for (let row = 0; row < rows; row++) matrix[row][0] = row;
  for (let col = 0; col < cols; col++) matrix[0][col] = col;

  for (let row = 1; row < rows; row++) {
    for (let col = 1; col < cols; col++) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost,
      );
    }
  }

  return matrix[rows - 1][cols - 1];
}

function getFuzzyScore(label: string, query: string): number | null {
  const normalizedLabel = normalizeForSearch(label);
  const normalizedQuery = normalizeForSearch(query);

  if (!normalizedQuery) return 0;
  if (normalizedLabel.includes(normalizedQuery)) return 0;

  const tokens = normalizedLabel.split(/\s+/).filter(Boolean);
  if (tokens.some((token) => token.includes(normalizedQuery))) return 1;

  let fromIndex = 0;
  let subsequenceMatched = true;
  let subsequenceGap = 0;

  for (const character of normalizedQuery) {
    const characterIndex = normalizedLabel.indexOf(character, fromIndex);
    if (characterIndex === -1) {
      subsequenceMatched = false;
      break;
    }

    subsequenceGap += Math.max(0, characterIndex - fromIndex);
    fromIndex = characterIndex + 1;
  }

  if (subsequenceMatched) {
    return 2 + subsequenceGap;
  }

  const distances = [getLevenshteinDistance(normalizedLabel, normalizedQuery)];
  for (const token of tokens) {
    distances.push(getLevenshteinDistance(token, normalizedQuery));
  }

  const minimumDistance = Math.min(...distances);
  const distanceThreshold = Math.max(1, Math.floor(normalizedQuery.length * 0.4));

  return minimumDistance <= distanceThreshold ? 100 + minimumDistance : null;
}

export function CommandPalette({ disabled = false }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserResult[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(false);
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const periodSearch = getPeriodNavigationSearch((location.search as Record<string, unknown>) ?? {});
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);

  const actions = useMemo(
    () =>
      navItems.map((item) => ({
        key: item.path,
        label: item.title,
        run: () => navigate({ to: item.path, search: periodSearch as never }),
      })),
    [navigate, periodSearch]
  );

  const filteredActions = useMemo(() => {
    if (!search.trim()) return actions;

    return actions
      .map((action) => ({
        action,
        score: getFuzzyScore(action.label, search),
      }))
      .filter((entry): entry is { action: (typeof actions)[number]; score: number } => entry.score !== null)
      .sort((left, right) => left.score - right.score || left.action.label.localeCompare(right.action.label))
      .map((entry) => entry.action);
  }, [actions, search]);

  const searchUsers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      requestIdRef.current++;
      setUsers([]);
      setUserLoading(false);
      setUserError(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setUserLoading(true);
    setUserError(false);

    try {
      const { data, error } = await supabase.rpc("get_admin_users_list", {
        p_search: query.trim(),
        p_plans: null,
        p_suspended: null,
        p_segments: null,
        p_churn_risks: null,
        p_engagement_min: null,
        p_engagement_max: null,
        p_created_from: null,
        p_created_to: null,
        p_last_access_from: null,
        p_last_access_to: null,
        p_menopause: null,
        p_biological_sex: null,
        p_sort_column: "name",
        p_sort_direction: "asc",
        p_page: 1,
        p_page_size: MAX_USER_RESULTS,
      });

      if (error) throw error;
      if (currentRequestId !== requestIdRef.current) return;

      const raw = data && typeof data === "object" && "users" in data
        ? (data as { users: unknown[] }).users
        : [];

      const mapped: UserResult[] = Array.isArray(raw)
        ? raw
            .filter((u): u is Record<string, unknown> => u !== null && typeof u === "object")
            .map((u) => ({
              id: String(u.user_id ?? u.id ?? ""),
              name: String(u.name ?? u.full_name ?? ""),
              email: String(u.email ?? ""),
              plan: typeof u.plan === "string" ? u.plan : null,
            }))
            .filter((u) => u.id)
        : [];

      setUsers(mapped);
    } catch {
      if (currentRequestId !== requestIdRef.current) return;
      setUserError(true);
      setUsers([]);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setUserLoading(false);
      }
    }
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void searchUsers(value);
      }, DEBOUNCE_MS);
    },
    [searchUsers]
  );

  useEffect(() => {
    if (disabled) return;

    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    }

    function onToggle() {
      setOpen((current) => !current);
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener(TOGGLE_EVENT, onToggle);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(TOGGLE_EVENT, onToggle);
    };
  }, [disabled]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      requestIdRef.current++;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
      setSearch("");
      setUsers([]);
      setUserLoading(false);
      setUserError(false);
    }
  }, [open]);

  useEffect(() => {
    const ref = requestIdRef;
    const debounce = debounceRef;
    return () => {
      ref.current++;
      if (debounce.current) {
        clearTimeout(debounce.current);
        debounce.current = undefined;
      }
    };
  }, []);

  function planBadgeClass(plan: string | null): string {
    if (plan === "mensal") return "badge-info";
    if (plan === "anual") return "badge-success";
    if (plan === "trial") return "badge-warning";
    return "badge-neutral";
  }

  return (
    <Command.Dialog
      open={!disabled && open}
      onOpenChange={setOpen}
      label="Command palette"
      className="fixed left-1/2 top-20 z-50 w-[640px] max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-lg border bg-card p-2 shadow-xl"
      overlayClassName="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
      shouldFilter={false}
    >
      <Command.Input
        className="h-11 w-full rounded-md border px-3 text-sm outline-none"
        placeholder="Buscar paginas ou usuarios..."
        value={search}
        onValueChange={handleSearchChange}
      />
      <Command.List className="mt-2 max-h-[360px] overflow-y-auto">
        <Command.Empty className="px-2 py-6 text-sm text-muted-foreground">Nenhum resultado.</Command.Empty>

        <Command.Group heading="Navegar">
          {filteredActions.map((action) => (
              <Command.Item
                key={action.key}
                value={`nav-${action.key}`}
                className="flex cursor-pointer items-center rounded px-2 py-2 text-sm data-[selected=true]:bg-muted"
                onSelect={() => {
                  setOpen(false);
                  action.run();
                }}
              >
                {action.label}
              </Command.Item>
            ))}
        </Command.Group>

        {search.trim().length >= 2 && (
          <Command.Group heading="Usuarios">
            {userLoading && (
              <Command.Loading>
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando usuarios...
                </div>
              </Command.Loading>
            )}

            {userError && (
              <div className="px-2 py-3 text-sm text-destructive">
                Erro ao buscar usuarios
              </div>
            )}

            {!userLoading && !userError && users.length === 0 && (
              <div className="px-2 py-3 text-sm text-muted-foreground">
                Nenhum usuario encontrado
              </div>
            )}

            {users.map((user) => (
              <Command.Item
                key={user.id}
                value={`user-${user.id}`}
                className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 text-sm data-[selected=true]:bg-muted"
                onSelect={() => {
                  setOpen(false);
                  void navigate({
                    to: "/users/$userId",
                    params: { userId: user.id },
                    search: periodSearch,
                  }).catch((error: unknown) => {
                    if (import.meta.env.DEV) {
                      console.error("[CommandPalette] failed to navigate to user profile", {
                        route: "/users/$userId",
                        userId: user.id,
                        error,
                      });
                    }
                  });
                }}
              >
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 truncate">
                  <span className="font-medium">{user.name}</span>
                  <span className="ml-2 text-muted-foreground">{user.email}</span>
                </div>
                <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${planBadgeClass(user.plan)}`}>
                  {user.plan ?? "Free"}
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
}
